import React, { useEffect, useState, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Divider,
  Badge,
  Avatar
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Notifications as NotificationsIcon,
  NotificationsActive as NotificationsActiveIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  Person as PersonIcon,
  Campaign as CampaignIcon,
  Schedule as ScheduleIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  Webhook as WebhookIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import axios from 'axios';
import { API_URL } from '../config';
import GoogleMCPNavigation from '../components/GoogleMCPNavigation';

interface AlertRule {
  id: number;
  alert_id: string;
  name: string;
  description: string;
  alert_type: string;
  metric: string;
  condition: string;
  threshold_value: number;
  campaign_id?: string;
  campaign_name?: string;
  is_active: boolean;
  notification_methods: string[];
  email_recipients?: string[];
  webhook_url?: string;
  frequency: string;
  last_triggered?: string;
  trigger_count: number;
  created_at: string;
  updated_at: string;
}

interface AlertFormData {
  name: string;
  description: string;
  alert_type: string;
  metric: string;
  condition: string;
  threshold_value: number;
  campaign_id?: string;
  is_active: boolean;
  notification_methods: string[];
  email_recipients: string[];
  webhook_url?: string;
  frequency: string;
}

interface AlertHistory {
  id: number;
  alert_id: string;
  triggered_at: string;
  metric_value: number;
  threshold_value: number;
  message: string;
  resolved_at?: string;
}

const GoogleMCPAlerts: React.FC = () => {
  const [alerts, setAlerts] = useState<AlertRule[]>([]);
  const [alertHistory, setAlertHistory] = useState<AlertHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [, setOpenHistoryModal] = useState(false);
  const [editingAlert, setEditingAlert] = useState<AlertRule | null>(null);
  const [formData, setFormData] = useState<AlertFormData>({
    name: '',
    description: '',
    alert_type: 'performance',
    metric: 'ctr',
    condition: 'below',
    threshold_value: 0,
    is_active: true,
    notification_methods: ['email'],
    email_recipients: [],
    frequency: 'immediate'
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning',
  });
  const [emailInput, setEmailInput] = useState('');

  const alertTypes = [
    { value: 'performance', label: 'Performance', icon: <TrendingUpIcon /> },
    { value: 'budget', label: 'Orçamento', icon: <MoneyIcon /> },
    { value: 'conversion', label: 'Conversão', icon: <PersonIcon /> },
    { value: 'campaign', label: 'Campanha', icon: <CampaignIcon /> },
    { value: 'system', label: 'Sistema', icon: <SettingsIcon /> }
  ];

  const metrics = {
    performance: [
      { value: 'ctr', label: 'CTR (%)' },
      { value: 'cpc', label: 'CPC (R$)' },
      { value: 'cpm', label: 'CPM (R$)' },
      { value: 'impressions', label: 'Impressões' },
      { value: 'clicks', label: 'Cliques' }
    ],
    budget: [
      { value: 'daily_spend', label: 'Gasto Diário (R$)' },
      { value: 'budget_utilization', label: 'Utilização do Orçamento (%)' },
      { value: 'cost_per_conversion', label: 'Custo por Conversão (R$)' }
    ],
    conversion: [
      { value: 'conversion_rate', label: 'Taxa de Conversão (%)' },
      { value: 'conversions', label: 'Número de Conversões' },
      { value: 'lead_quality_score', label: 'Score de Qualidade do Lead' }
    ],
    campaign: [
      { value: 'campaign_status', label: 'Status da Campanha' },
      { value: 'keyword_performance', label: 'Performance de Palavras-chave' }
    ],
    system: [
      { value: 'api_errors', label: 'Erros de API' },
      { value: 'sync_status', label: 'Status de Sincronização' }
    ]
  };

  const conditions = [
    { value: 'above', label: 'Acima de' },
    { value: 'below', label: 'Abaixo de' },
    { value: 'equals', label: 'Igual a' },
    { value: 'change_increase', label: 'Aumento de' },
    { value: 'change_decrease', label: 'Diminuição de' }
  ];

  const notificationMethods = [
    { value: 'email', label: 'Email', icon: <EmailIcon /> },
    { value: 'sms', label: 'SMS', icon: <SmsIcon /> },
    { value: 'webhook', label: 'Webhook', icon: <WebhookIcon /> }
  ];

  const frequencies = [
    { value: 'immediate', label: 'Imediato' },
    { value: 'hourly', label: 'A cada hora' },
    { value: 'daily', label: 'Diário' },
    { value: 'weekly', label: 'Semanal' }
  ];

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/google-mcp/alerts`);
      setAlerts(response.data.data || []);
    } catch (error: any) {
      console.error('Erro ao carregar alertas:', error);
      showSnackbar('Erro ao carregar alertas', 'error');
      // Dados simulados para desenvolvimento
      setAlerts([
        {
          id: 1,
          alert_id: 'alert_001',
          name: 'CTR Baixo - Consórcio Carros',
          description: 'Alerta quando CTR da campanha de carros fica abaixo de 2%',
          alert_type: 'performance',
          metric: 'ctr',
          condition: 'below',
          threshold_value: 2.0,
          campaign_id: 'camp_001',
          campaign_name: 'Consórcio Carros - Geral',
          is_active: true,
          notification_methods: ['email', 'webhook'],
          email_recipients: ['admin@leadssearch.com'],
          webhook_url: 'https://webhook.site/test',
          frequency: 'immediate',
          last_triggered: '2024-01-15T14:30:00Z',
          trigger_count: 3,
          created_at: '2024-01-01T00:00:00Z',
          updated_at: '2024-01-15T14:30:00Z'
        },
        {
          id: 2,
          alert_id: 'alert_002',
          name: 'Orçamento Alto - Geral',
          description: 'Alerta quando gasto diário ultrapassa R$ 1000',
          alert_type: 'budget',
          metric: 'daily_spend',
          condition: 'above',
          threshold_value: 1000,
          is_active: true,
          notification_methods: ['email', 'sms'],
          email_recipients: ['admin@leadssearch.com', 'financeiro@leadssearch.com'],
          frequency: 'immediate',
          trigger_count: 1,
          created_at: '2024-01-01T00:00:00Z',
          updated_at: '2024-01-10T09:15:00Z'
        },
        {
          id: 3,
          alert_id: 'alert_003',
          name: 'Conversões Baixas - Imóveis',
          description: 'Alerta quando taxa de conversão fica abaixo de 3%',
          alert_type: 'conversion',
          metric: 'conversion_rate',
          condition: 'below',
          threshold_value: 3.0,
          campaign_id: 'camp_002',
          campaign_name: 'Consórcio Imóveis - Premium',
          is_active: false,
          notification_methods: ['email'],
          email_recipients: ['marketing@leadssearch.com'],
          frequency: 'daily',
          trigger_count: 0,
          created_at: '2024-01-05T00:00:00Z',
          updated_at: '2024-01-05T00:00:00Z'
        }
      ]);
    }
    setLoading(false);
  }, []);

  const fetchAlertHistory = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/google-mcp/alerts/history`);
      setAlertHistory(response.data.data || []);
    } catch (error: any) {
      console.error('Erro ao carregar histórico:', error);
      // Dados simulados para desenvolvimento
      setAlertHistory([
        {
          id: 1,
          alert_id: 'alert_001',
          triggered_at: '2024-01-15T14:30:00Z',
          metric_value: 1.8,
          threshold_value: 2.0,
          message: 'CTR da campanha "Consórcio Carros - Geral" está em 1.8%, abaixo do limite de 2.0%',
          resolved_at: '2024-01-15T16:00:00Z'
        },
        {
          id: 2,
          alert_id: 'alert_002',
          triggered_at: '2024-01-10T09:15:00Z',
          metric_value: 1250.75,
          threshold_value: 1000,
          message: 'Gasto diário de R$ 1.250,75 ultrapassou o limite de R$ 1.000,00'
        }
      ]);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
    fetchAlertHistory();
  }, [fetchAlerts, fetchAlertHistory]);

  const handleOpenModal = (alertToEdit?: AlertRule) => {
    if (alertToEdit) {
      setEditingAlert(alertToEdit);
      setFormData({
        name: alertToEdit.name,
        description: alertToEdit.description,
        alert_type: alertToEdit.alert_type,
        metric: alertToEdit.metric,
        condition: alertToEdit.condition,
        threshold_value: alertToEdit.threshold_value,
        campaign_id: alertToEdit.campaign_id,
        is_active: alertToEdit.is_active,
        notification_methods: alertToEdit.notification_methods,
        email_recipients: alertToEdit.email_recipients || [],
        webhook_url: alertToEdit.webhook_url,
        frequency: alertToEdit.frequency
      });
    } else {
      setEditingAlert(null);
      setFormData({
        name: '',
        description: '',
        alert_type: 'performance',
        metric: 'ctr',
        condition: 'below',
        threshold_value: 0,
        is_active: true,
        notification_methods: ['email'],
        email_recipients: [],
        frequency: 'immediate'
      });
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingAlert(null);
    setEmailInput('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingAlert) {
        await axios.put(`${API_URL}/api/google-mcp/alerts/${editingAlert.id}`, formData);
        showSnackbar('Alerta atualizado com sucesso!', 'success');
      } else {
        await axios.post(`${API_URL}/api/google-mcp/alerts`, formData);
        showSnackbar('Alerta criado com sucesso!', 'success');
      }
      
      handleCloseModal();
      fetchAlerts();
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Erro ao salvar alerta';
      showSnackbar(message, 'error');
    }
    setLoading(false);
  };

  const toggleAlertStatus = async (alert: AlertRule) => {
    try {
      await axios.patch(`${API_URL}/api/google-mcp/alerts/${alert.id}/toggle`, {
        is_active: !alert.is_active
      });
      showSnackbar(`Alerta ${!alert.is_active ? 'ativado' : 'desativado'} com sucesso!`, 'success');
      fetchAlerts();
    } catch (error: any) {
      showSnackbar('Erro ao alterar status do alerta', 'error');
    }
  };

  const deleteAlert = async (alertId: number) => {
    if (window.confirm('Tem certeza que deseja excluir este alerta?')) {
      try {
        await axios.delete(`${API_URL}/api/google-mcp/alerts/${alertId}`);
        showSnackbar('Alerta excluído com sucesso!', 'success');
        fetchAlerts();
      } catch (error: any) {
        showSnackbar('Erro ao excluir alerta', 'error');
      }
    }
  };

  const testAlert = async (alertId: number) => {
    try {
      await axios.post(`${API_URL}/api/google-mcp/alerts/${alertId}/test`);
      showSnackbar('Teste de alerta enviado com sucesso!', 'success');
    } catch (error: any) {
      showSnackbar('Erro ao testar alerta', 'error');
    }
  };

  const addEmailRecipient = () => {
    if (emailInput.trim() && !formData.email_recipients.includes(emailInput.trim())) {
      setFormData({
        ...formData,
        email_recipients: [...formData.email_recipients, emailInput.trim()]
      });
      setEmailInput('');
    }
  };

  const removeEmailRecipient = (email: string) => {
    setFormData({
      ...formData,
      email_recipients: formData.email_recipients.filter(e => e !== email)
    });
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({ open: true, message, severity });
  };

  const getAlertTypeIcon = (type: string) => {
    const alertType = alertTypes.find(t => t.value === type);
    return alertType?.icon || <NotificationsIcon />;
  };

  const getAlertSeverityColor = (alert: AlertRule) => {
    if (!alert.is_active) return 'default';
    if (alert.alert_type === 'budget' || alert.alert_type === 'system') return 'error';
    if (alert.alert_type === 'performance') return 'warning';
    return 'info';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const formatThreshold = (metric: string, value: number) => {
    if (metric.includes('rate') || metric === 'ctr') {
      return `${value}%`;
    }
    if (metric.includes('cost') || metric.includes('spend') || metric === 'cpc' || metric === 'cpm') {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(value);
    }
    return value.toLocaleString('pt-BR');
  };

  const getActiveAlertsCount = () => alerts.filter(a => a.is_active).length;
  const getTriggeredAlertsCount = () => alerts.filter(a => a.trigger_count > 0).length;

  return (
    <Container maxWidth="lg">
      {/* Navegação */}
      <GoogleMCPNavigation currentPage="alerts" />
      
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Alertas Google MCP
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenModal()}
          >
            Novo Alerta
          </Button>
        </Box>
        <Typography variant="subtitle1" color="text.secondary">
          Configure alertas para monitorar performance e receber notificações
        </Typography>
      </Box>

      {/* Resumo dos Alertas */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Badge badgeContent={getActiveAlertsCount()} color="primary">
                  <NotificationsActiveIcon color="primary" sx={{ mr: 2, fontSize: 40 }} />
                </Badge>
                <Box sx={{ ml: 1 }}>
                  <Typography variant="h4">{alerts.length}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total de Alertas
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircleIcon color="success" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography variant="h4">{getActiveAlertsCount()}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Alertas Ativos
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <WarningIcon color="warning" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography variant="h4">{getTriggeredAlertsCount()}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Alertas Disparados
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ScheduleIcon color="info" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography variant="h4">{alertHistory.length}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Histórico Total
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Lista de Alertas */}
      <Paper sx={{ mb: 4 }}>
        <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">Alertas Configurados</Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Alerta</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Métrica</TableCell>
                <TableCell>Condição</TableCell>
                <TableCell>Campanha</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Disparos</TableCell>
                <TableCell>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && alerts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : alerts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary">
                      Nenhum alerta configurado
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                alerts.map((alert) => (
                  <TableRow key={alert.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ mr: 2, bgcolor: `${getAlertSeverityColor(alert)}.main` }}>
                          {getAlertTypeIcon(alert.alert_type)}
                        </Avatar>
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            {alert.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {alert.description}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={alertTypes.find(t => t.value === alert.alert_type)?.label || alert.alert_type}
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {metrics[alert.alert_type as keyof typeof metrics]?.find(m => m.value === alert.metric)?.label || alert.metric}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {conditions.find(c => c.value === alert.condition)?.label} {formatThreshold(alert.metric, alert.threshold_value)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {alert.campaign_name ? (
                        <Chip label={alert.campaign_name} variant="outlined" size="small" />
                      ) : (
                        <Typography variant="body2" color="text.secondary">Todas</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {alert.is_active ? (
                          <Chip label="Ativo" color="success" size="small" />
                        ) : (
                          <Chip label="Inativo" color="default" size="small" />
                        )}
                        <Switch
                          checked={alert.is_active}
                          onChange={() => toggleAlertStatus(alert)}
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {alert.trigger_count}
                        </Typography>
                        {alert.last_triggered && (
                          <Typography variant="caption" color="text.secondary">
                            Último: {formatDate(alert.last_triggered)}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenModal(alert)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Testar">
                          <IconButton
                            size="small"
                            onClick={() => testAlert(alert.id)}
                            color="info"
                          >
                            <NotificationsIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton
                            size="small"
                            onClick={() => deleteAlert(alert.id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Histórico Recente */}
      <Paper>
        <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Histórico Recente</Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setOpenHistoryModal(true)}
            >
              Ver Histórico Completo
            </Button>
          </Box>
        </Box>
        <List>
          {alertHistory.slice(0, 5).map((history, index) => (
            <React.Fragment key={history.id}>
              <ListItem>
                <ListItemIcon>
                  <Avatar sx={{ bgcolor: history.resolved_at ? 'success.main' : 'warning.main' }}>
                    {history.resolved_at ? <CheckCircleIcon /> : <WarningIcon />}
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={history.message}
                  secondary={
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Disparado em: {formatDate(history.triggered_at)}
                      </Typography>
                      {history.resolved_at && (
                        <Typography variant="caption" color="success.main" sx={{ ml: 2 }}>
                          Resolvido em: {formatDate(history.resolved_at)}
                        </Typography>
                      )}
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <Chip
                    label={history.resolved_at ? 'Resolvido' : 'Ativo'}
                    color={history.resolved_at ? 'success' : 'warning'}
                    size="small"
                  />
                </ListItemSecondaryAction>
              </ListItem>
              {index < alertHistory.slice(0, 5).length - 1 && <Divider />}
            </React.Fragment>
          ))}
          {alertHistory.length === 0 && (
            <ListItem>
              <ListItemText
                primary="Nenhum alerta foi disparado ainda"
                secondary="Os alertas aparecerão aqui quando forem acionados"
              />
            </ListItem>
          )}
        </List>
      </Paper>

      {/* Modal de Criação/Edição */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {editingAlert ? 'Editar Alerta' : 'Novo Alerta'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Nome do Alerta"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Descrição"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  multiline
                  rows={2}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Tipo de Alerta</InputLabel>
                  <Select
                    value={formData.alert_type}
                    label="Tipo de Alerta"
                    onChange={(e) => {
                      const newType = e.target.value;
                      setFormData({ 
                        ...formData, 
                        alert_type: newType,
                        metric: metrics[newType as keyof typeof metrics]?.[0]?.value || ''
                      });
                    }}
                  >
                    {alertTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {type.icon}
                          <Typography sx={{ ml: 1 }}>{type.label}</Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Métrica</InputLabel>
                  <Select
                    value={formData.metric}
                    label="Métrica"
                    onChange={(e) => setFormData({ ...formData, metric: e.target.value })}
                  >
                    {(metrics[formData.alert_type as keyof typeof metrics] || []).map((metric) => (
                      <MenuItem key={metric.value} value={metric.value}>
                        {metric.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Condição</InputLabel>
                  <Select
                    value={formData.condition}
                    label="Condição"
                    onChange={(e) => setFormData({ ...formData, condition: e.target.value })}
                  >
                    {conditions.map((condition) => (
                      <MenuItem key={condition.value} value={condition.value}>
                        {condition.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Valor Limite"
                  type="number"
                  value={formData.threshold_value}
                  onChange={(e) => setFormData({ ...formData, threshold_value: parseFloat(e.target.value) })}
                  required
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Frequência</InputLabel>
                  <Select
                    value={formData.frequency}
                    label="Frequência"
                    onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                  >
                    {frequencies.map((freq) => (
                      <MenuItem key={freq.value} value={freq.value}>
                        {freq.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Métodos de Notificação
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {notificationMethods.map((method) => (
                    <FormControlLabel
                      key={method.value}
                      control={
                        <Switch
                          checked={formData.notification_methods.includes(method.value)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                notification_methods: [...formData.notification_methods, method.value]
                              });
                            } else {
                              setFormData({
                                ...formData,
                                notification_methods: formData.notification_methods.filter(m => m !== method.value)
                              });
                            }
                          }}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {method.icon}
                          <Typography sx={{ ml: 1 }}>{method.label}</Typography>
                        </Box>
                      }
                    />
                  ))}
                </Box>
              </Grid>
              
              {formData.notification_methods.includes('email') && (
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Destinatários de Email
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <TextField
                      fullWidth
                      label="Adicionar email"
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addEmailRecipient();
                        }
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Button onClick={addEmailRecipient} size="small">
                              Adicionar
                            </Button>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {formData.email_recipients.map((email, index) => (
                      <Chip
                        key={index}
                        label={email}
                        onDelete={() => removeEmailRecipient(email)}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Grid>
              )}
              
              {formData.notification_methods.includes('webhook') && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="URL do Webhook"
                    value={formData.webhook_url || ''}
                    onChange={(e) => setFormData({ ...formData, webhook_url: e.target.value })}
                    placeholder="https://seu-webhook.com/endpoint"
                  />
                </Grid>
              )}
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    />
                  }
                  label="Alerta ativo"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal}>Cancelar</Button>
            <Button 
              type="submit" 
              variant="contained"
              disabled={loading}
            >
              {loading ? 'Salvando...' : (editingAlert ? 'Atualizar' : 'Criar')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default GoogleMCPAlerts;